import { useState } from "react";
import { toast } from "sonner";
import { Instagram, Mail } from "lucide-react";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";
import { sendTransmission } from "../lib/api";

const OFFICIAL_EMAIL = "cosmicdreamsuniverse@gmail.com";

const SOCIALS = [
  { icon: Instagram, label: "Instagram — @cosmicdreams_universe", testid: "contact-social-instagram", href: "https://instagram.com/cosmicdreams_universe", external: true },
  { icon: Mail, label: `Email — ${OFFICIAL_EMAIL}`, testid: "contact-social-email", href: `mailto:${OFFICIAL_EMAIL}`, external: false },
];

const inputCls =
  "w-full border-b border-white/15 bg-transparent py-3 text-sm font-light text-[#F5F5FA] placeholder:text-[#606070] focus:border-[#D4AF37]/70 focus:outline-none transition-colors duration-500";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendTransmission(form);
      toast.success("Message received. Thank you for reaching out.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Transmission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative z-10 px-6 py-28 md:px-16 lg:px-24 lg:py-40">
      <SectionHeading
        title="CONTACT"
        eyebrow="Open a channel. For collaborations, film enquiries, rights, or simply to reach the creator of the universe."
      />
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24">
        <Reveal>
          <div className="space-y-10">
            <div>
              <span className="font-mono text-[10px] tracking-[0.4em] text-[#606070] uppercase">Origin</span>
              <p className="mt-3 text-sm font-light text-[#A0A0B0]">
                The Cosmic Dreams Universe — official digital home
              </p>
            </div>
            <div>
              <span className="font-mono text-[10px] tracking-[0.4em] text-[#606070] uppercase">Email</span>
              <p className="mt-3">
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${OFFICIAL_EMAIL}`}
                  className="text-sm font-light text-[#A0A0B0] transition-colors duration-500 hover:text-[#D4AF37]"
                >
                  {OFFICIAL_EMAIL}
                </a>
              </p>
            </div>
            <div>
              <span className="font-mono text-[10px] tracking-[0.4em] text-[#606070] uppercase">Follow the Signal</span>
              <div className="mt-4 flex gap-4">
                {SOCIALS.map(({ icon: Icon, label, testid, href, external }) => (
                  <a
                    key={testid}
                    data-testid={testid}
                    href={href}
                    aria-label={label}
                    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#A0A0B0] transition-all duration-500 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
                  >
                    <Icon size={16} strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
            <div>
              <label className="font-mono text-[10px] tracking-[0.35em] text-[#A0A0B0] uppercase" htmlFor="contact-name">
                Name
              </label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
                placeholder="Traveller of the universe"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.35em] text-[#A0A0B0] uppercase" htmlFor="contact-email">
                Email
              </label>
              <input
                id="contact-email"
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                placeholder="Your email address"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.35em] text-[#A0A0B0] uppercase" htmlFor="contact-subject">
                Subject
              </label>
              <input
                id="contact-subject"
                data-testid="contact-subject-input"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className={inputCls}
                placeholder="What is this regarding?"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.35em] text-[#A0A0B0] uppercase" htmlFor="contact-message">
                Message
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputCls} resize-none`}
                placeholder="Speak, and the universe will listen."
              />
            </div>
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="group relative overflow-hidden border border-white/20 px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-white transition-colors duration-700 hover:border-[#D4AF37]/70 hover:text-[#D4AF37] disabled:opacity-50"
            >
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[#D4AF37]/10 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
              {sending ? "TRANSMITTING…" : "SEND MESSAGE"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
